// Admin > Festival — Tamil festivals, Tamil Nadu Government holidays and
// Tamil Muhurtham dates. Base data lives in src/data/festival-2026.json (no
// DB — see root CLAUDE.md's DB-safety rule, and the enhancement conversation
// that chose this over a DB-backed table). Add/Edit here can't write back to
// that bundled file at runtime, so edits are kept as a localStorage overlay
// (src/data/festivalOverlay.ts) merged over the base JSON on render (persists
// per-browser across reloads, and is picked up by the topbar's
// FestivalNotification too), with a "Copy updated JSON" action so a developer
// can paste the merged list into festival-2026.json to make a change
// permanent for everyone/every browser.
//
// NOTE: festival-2026.json currently only has the 3 sample rows confirmed
// against the reference site's screenshot (Jan 1, 3, 28). The rest of 2026
// (and future years) needs to be filled into that JSON file directly — see
// the file's header comment.
import * as React from "react";
import moment from "moment";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Pencil, ClipboardCopy, Check } from "lucide-react";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Label } from "ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "ui/select";
import ApparelMenu from "../Components/SubComponents/apparelMenu";
import festivalData from "../data/festival-2026.json";
import { getFestivalImage } from "../data/festivalImages";
import {
    FestivalCategory,
    FestivalEntry,
    FestivalOverlay,
    RawFestivalEntry,
    loadFestivalOverlay,
    saveFestivalOverlay,
    mergeFestivalOverlay,
} from "../data/festivalOverlay";

const CATEGORY_SECTIONS: { key: FestivalCategory; title: string; emptyText: string }[] = [
    { key: "Tamil Festival", title: "Tamil Festivals", emptyText: "No Tamil festivals this month." },
    { key: "Holiday", title: "Tamil Nadu Government Holidays", emptyText: "No government holidays this month." },
    { key: "Muhurtham", title: "Tamil Muhurtham Dates", emptyText: "No Muhurtham dates this month." },
];

const CATEGORY_OPTIONS: FestivalCategory[] = ["Tamil Festival", "Holiday", "Muhurtham"];

function FestivalCard({ entry, onEdit }: { entry: FestivalEntry; onEdit: (entry: FestivalEntry) => void }) {
    const image = getFestivalImage(entry.englishName, entry.category);
    return (
        <div className="group flex items-start gap-3 rounded-xl border p-3">
            {image.type === "photo" ? (
                <img src={image.url} alt={entry.englishName} className="h-12 w-12 shrink-0 rounded-lg object-cover ring-2 ring-violet-200" />
            ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-2xl ring-2 ring-violet-200">
                    {image.value}
                </span>
            )}
            <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-foreground">{entry.tamilName}</p>
                <p className="truncate text-xs text-muted-foreground">{entry.englishName}</p>
                <p className="text-xs text-muted-foreground">{moment(entry.date).format("DD-MMM-YYYY")} · {entry.day}</p>
                {entry.tamilDate && <p className="text-xs text-violet-600">{entry.tamilDate}</p>}
            </div>
            <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                aria-label={`Edit ${entry.englishName}`}
                onClick={() => onEdit(entry)}
            >
                <Pencil className="h-3.5 w-3.5" />
            </Button>
        </div>
    );
}

type FormState = {
    date: string;
    tamilName: string;
    englishName: string;
    category: FestivalCategory;
    tamilDate: string;
};

const EMPTY_FORM: FormState = { date: "", tamilName: "", englishName: "", category: "Tamil Festival", tamilDate: "" };

function FestivalFormDialog({
    open,
    onOpenChange,
    editingEntry,
    onSave,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    editingEntry: FestivalEntry | null;
    onSave: (form: FormState) => void;
}) {
    const [form, setForm] = React.useState<FormState>(EMPTY_FORM);

    React.useEffect(() => {
        if (!open) return;
        if (editingEntry) {
            setForm({
                date: editingEntry.date,
                tamilName: editingEntry.tamilName,
                englishName: editingEntry.englishName,
                category: editingEntry.category,
                tamilDate: editingEntry.tamilDate ?? "",
            });
        } else {
            setForm(EMPTY_FORM);
        }
    }, [open, editingEntry]);

    const isValid = form.date && form.tamilName.trim() && form.englishName.trim();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{editingEntry ? "Edit Festival" : "Add Festival"}</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-2">
                    <div className="grid gap-1.5">
                        <Label htmlFor="festival-date">Date</Label>
                        <Input
                            id="festival-date"
                            type="date"
                            value={form.date}
                            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="festival-tamil-name">Tamil Name</Label>
                        <Input
                            id="festival-tamil-name"
                            value={form.tamilName}
                            onChange={(e) => setForm((f) => ({ ...f, tamilName: e.target.value }))}
                            placeholder="தைப் பொங்கல்"
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="festival-english-name">English Name</Label>
                        <Input
                            id="festival-english-name"
                            value={form.englishName}
                            onChange={(e) => setForm((f) => ({ ...f, englishName: e.target.value }))}
                            placeholder="Thai Pongal"
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="festival-category">Category</Label>
                        <Select value={form.category} onValueChange={(value) => setForm((f) => ({ ...f, category: value as FestivalCategory }))}>
                            <SelectTrigger id="festival-category">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CATEGORY_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="festival-tamil-date">Tamil Calendar Date (optional)</Label>
                        <Input
                            id="festival-tamil-date"
                            value={form.tamilDate}
                            onChange={(e) => setForm((f) => ({ ...f, tamilDate: e.target.value }))}
                            placeholder="தை 14"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button disabled={!isValid} onClick={() => onSave(form)}>
                        {editingEntry ? "Save Changes" : "Add Festival"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const Festival = () => {
    const [cursor, setCursor] = React.useState(() => moment());
    const [overlay, setOverlay] = React.useState<FestivalOverlay>(() => loadFestivalOverlay());
    const [formOpen, setFormOpen] = React.useState(false);
    const [editingEntry, setEditingEntry] = React.useState<FestivalEntry | null>(null);
    const [copied, setCopied] = React.useState(false);

    const allEntries = React.useMemo<FestivalEntry[]>(
        () => mergeFestivalOverlay(festivalData as RawFestivalEntry[], overlay),
        [overlay]
    );

    const entriesThisMonth = React.useMemo(
        () => allEntries.filter((e) => moment(e.date).isSame(cursor, "month")),
        [allEntries, cursor]
    );

    const persistOverlay = (next: FestivalOverlay) => {
        setOverlay(next);
        saveFestivalOverlay(next);
    };

    const openAddForm = () => {
        setEditingEntry(null);
        setFormOpen(true);
    };

    const openEditForm = (entry: FestivalEntry) => {
        setEditingEntry(entry);
        setFormOpen(true);
    };

    const handleSave = (form: FormState) => {
        const raw: RawFestivalEntry = {
            date: form.date,
            day: moment(form.date).format("dddd"),
            tamilName: form.tamilName.trim(),
            englishName: form.englishName.trim(),
            category: form.category,
            ...(form.tamilDate.trim() ? { tamilDate: form.tamilDate.trim() } : {}),
        };

        if (editingEntry) {
            if (editingEntry.id.startsWith("base-")) {
                persistOverlay({
                    ...overlay,
                    edits: { ...overlay.edits, [editingEntry.id]: raw },
                });
            } else {
                persistOverlay({
                    ...overlay,
                    additions: overlay.additions.map((a) => (a.id === editingEntry.id ? { id: a.id, ...raw } : a)),
                });
            }
        } else {
            const newEntry: FestivalEntry = { id: `custom-${Date.now()}`, ...raw };
            persistOverlay({ ...overlay, additions: [...overlay.additions, newEntry] });
        }
        toast.success(editingEntry ? "Festival updated successfully" : "Festival added successfully");
        setFormOpen(false);
    };

    const copyMergedJson = async () => {
        const sorted = [...allEntries]
            .map(({ id, ...rest }) => rest)
            .sort((a, b) => a.date.localeCompare(b.date));
        try {
            await navigator.clipboard.writeText(JSON.stringify(sorted, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // clipboard access can be blocked (permissions/non-secure context) — nothing more to do here
        }
    };

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="festival" />

            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h1 className="text-2xl font-semibold text-foreground">Festival</h1>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => setCursor((c) => c.clone().subtract(1, "month"))} aria-label="Previous month">
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="w-36 text-center text-sm font-medium text-foreground">{cursor.format("MMMM YYYY")}</span>
                    <Button variant="outline" size="icon" onClick={() => setCursor((c) => c.clone().add(1, "month"))} aria-label="Next month">
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button variant="link" className="h-auto p-0 text-sm" onClick={() => setCursor(moment())}>
                        Today
                    </Button>
                    {/* <Button variant="outline" size="sm" onClick={copyMergedJson}>
                        {copied ? <Check className="mr-1.5 h-4 w-4" /> : <ClipboardCopy className="mr-1.5 h-4 w-4" />}
                        {copied ? "Copied" : "Copy JSON"}
                    </Button> */}
                    <Button size="sm" onClick={openAddForm}>
                        <Plus className="mr-1.5 h-4 w-4" />
                        Add Festival
                    </Button>
                </div>
            </div>

            {(Object.keys(overlay.edits).length > 0 || overlay.additions.length > 0) && (
                <p className="mb-4 text-xs text-muted-foreground">
                    You have unsaved changes stored in this browser only. Use "Copy JSON" and hand it to a developer to
                    update <code>festival-2026.json</code> so the change applies for everyone.
                </p>
            )}

            <div className="grid gap-4 lg:grid-cols-3">
                {CATEGORY_SECTIONS.map((section) => {
                    const rows = entriesThisMonth
                        .filter((e) => e.category === section.key)
                        .sort((a, b) => a.date.localeCompare(b.date));
                    return (
                        <div key={section.key} className="rounded-2xl border border-t-4 border-t-violet-400 p-4">
                            <h2 className="mb-3 text-sm font-semibold text-violet-700">{section.title}</h2>
                            {rows.length === 0 ? (
                                <p className="text-sm text-muted-foreground">{section.emptyText}</p>
                            ) : (
                                <div className="space-y-2">
                                    {rows.map((entry) => (
                                        <FestivalCard key={entry.id} entry={entry} onEdit={openEditForm} />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <FestivalFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                editingEntry={editingEntry}
                onSave={handleSave}
            />
        </div>
    );
};

export default Festival;
